import {
  dossierCreation,
  formaterJour,
  formaterQuotePart,
  type OrdreDossier,
  refacturationDossier,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type CSSProperties, type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { api, corps, type Dossier, gere, lire, requeteMoi, requetes } from '../api'
import { Icone } from '../composants/Icone'
import { Vide } from '../composants/Section'
import { LIBELLES_ORDRE_DOSSIER, options } from '../libelles'
import { confirmer } from '../composants/confirmation'

// Plan de classement d'une entité : l'arbre de ses dossiers, chacun avec à quoi il sert et son
// principe d'ordre, modifiables en place. Claude le lit pour placer chaque pièce ; il ne crée
// aucun dossier : ce qu'il propose attend ici qu'un humain le valide ou le refuse.

const message = (e: unknown) => (e instanceof Error ? e.message : 'Enregistrement impossible.')

/** Texte modifiable en place : un clic pour l'ouvrir, Entrée ou sortie du champ pour garder. */
function TexteEnLigne({
  valeur,
  onEnregistrer,
  libelle,
  vide = 'À renseigner',
  multiligne = false,
  lectureSeule = false,
}: {
  valeur: string | null
  onEnregistrer: (v: string) => Promise<unknown>
  libelle: string
  vide?: string
  multiligne?: boolean
  lectureSeule?: boolean
}) {
  const [edite, setEdite] = useState(false)
  const [brouillon, setBrouillon] = useState(valeur ?? '')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const champ = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  useEffect(() => {
    if (edite) champ.current?.focus()
  }, [edite])

  if (lectureSeule || !edite)
    return (
      <span className="en-ligne-texte">
        {lectureSeule ? (
          <span className={valeur ? undefined : 'discret'}>{valeur || vide}</span>
        ) : (
          <button
            type="button"
            className="en-ligne"
            aria-label={`${libelle} : modifier`}
            onClick={() => {
              setBrouillon(valeur ?? '')
              setErreur(null)
              setEdite(true)
            }}
          >
            <span className={valeur ? undefined : 'discret'}>{valeur || vide}</span>
            <Icone nom="edit" />
          </button>
        )}
      </span>
    )

  const garder = async () => {
    if (enCours) return
    if (brouillon.trim() === (valeur ?? '').trim()) {
      setEdite(false)
      return
    }
    setEnCours(true)
    try {
      await onEnregistrer(brouillon.trim())
      setErreur(null)
      setEdite(false)
    } catch (e) {
      setErreur(message(e))
    } finally {
      setEnCours(false)
    }
  }
  const touche = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setEdite(false)
      setErreur(null)
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void garder()
    }
  }
  const proprietes = {
    ref: champ,
    value: brouillon,
    disabled: enCours,
    'aria-label': libelle,
    onChange: (e: { target: { value: string } }) => setBrouillon(e.target.value),
    onBlur: () => void garder(),
    onKeyDown: touche,
  }
  return (
    <span className="en-ligne-champ">
      {multiligne ? <textarea rows={2} {...proprietes} /> : <input {...proprietes} />}
      {erreur && <span className="alerte">{erreur}</span>}
    </span>
  )
}

export function PlanClassement({ id }: { id: string }) {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const entite = useQuery(requetes.entite(id))
  const plan = useQuery(requetes.plan(id))
  const [erreur, setErreur] = useState<string | null>(null)
  const rafraichir = () => client.invalidateQueries({ queryKey: ['plan', id] })

  if (entite.isPending || plan.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (entite.isError || plan.isError || !entite.data || !plan.data)
    return (
      <main>
        <p className="alerte">Entité introuvable.</p>
        <Link to="/entites">Retour aux entités</Link>
      </main>
    )
  const e = entite.data
  const { dossiers, propositions } = plan.data
  const ecriture = gere(moi, id) && !e.archivee

  return (
    <main>
      <header className="entete-fiche">
        <p className="fil">
          <Link to="/entites">Entités</Link> ·{' '}
          <Link to="/entites/$id" params={{ id }}>
            {e.nom}
          </Link>
        </p>
        <h1>Plan de classement</h1>
        <p className="discret">
          Chaque dossier dit à quoi il sert et comment il s’ordonne. Claude lit ce plan avant de
          placer une pièce ; il ne crée aucun dossier : il en propose, et rien ne change sans vous.
        </p>
      </header>

      {!e.racineClassement && (
        <p className="carte bandeau-plan ton-ambre">
          Sans racine de classement, les pièces de {e.nom} suivent encore les anciens chemins. La
          renseigner sur la{' '}
          <Link to="/entites/$id" params={{ id }}>
            fiche de l’entité
          </Link>{' '}
          pour ranger selon ce plan.
        </p>
      )}
      {propositions > 0 && (
        <div className="carte bandeau-plan bandeau-plan-propositions ton-ok">
          <Icone nom="smart_toy" />
          <span>
            {propositions === 1
              ? 'Une proposition de l’assistant attend votre décision, ci-dessous.'
              : `${propositions} propositions de l’assistant attendent votre décision, ci-dessous.`}
          </span>
          {ecriture && propositions > 1 && (
            <button
              type="button"
              className="bouton bandeau-plan-action"
              onClick={async () => {
                if (!(await confirmer(`Valider les ${propositions} propositions de l’assistant ?`)))
                  return
                try {
                  await lire(api.entites[':id'].plan['valider-tout'].$post({ param: { id } }))
                  await rafraichir()
                } catch (x) {
                  setErreur(message(x))
                }
              }}
            >
              <Icone nom="check" />
              Tout valider
            </button>
          )}
        </div>
      )}
      {erreur && dossiers.length > 0 && <p className="alerte">{erreur}</p>}

      <section className="carte plan">
        {dossiers.length === 0 ? (
          <div className="pile">
            <Vide>Cette entité n’a pas encore de plan de classement.</Vide>
            {ecriture && (
              <div>
                <button
                  type="button"
                  className="bouton"
                  onClick={async () => {
                    try {
                      await lire(api.entites[':id'].plan.$post({ param: { id } }))
                      await rafraichir()
                    } catch (x) {
                      setErreur(message(x))
                    }
                  }}
                >
                  <Icone nom="create_new_folder" />
                  Créer le plan de départ
                </button>
              </div>
            )}
            {erreur && <p className="alerte">{erreur}</p>}
          </div>
        ) : (
          dossiers.map((d) => (
            <LigneDossier
              key={d.id}
              d={d}
              dossiers={dossiers}
              entiteId={id}
              racine={e.racineClassement}
              ecriture={ecriture}
              rafraichir={rafraichir}
            />
          ))
        )}
      </section>
    </main>
  )
}

const PEUT_CONTENIR: readonly OrdreDossier[] = ['sous_dossiers', 'par_exercice', 'par_annee']
const date = (o: OrdreDossier) => o === 'par_exercice' || o === 'par_annee'

function LigneDossier({
  d,
  dossiers,
  entiteId,
  racine,
  ecriture,
  rafraichir,
}: {
  d: Dossier
  dossiers: readonly Dossier[]
  entiteId: string
  racine: string | null
  ecriture: boolean
  rafraichir: () => Promise<unknown>
}) {
  const [erreur, setErreur] = useState<string | null>(null)
  const [ajout, setAjout] = useState(false)
  const [deplacer, setDeplacer] = useState(false)
  const estRacine = d.parentId === null
  const parent = dossiers.find((x) => x.id === d.parentId)
  const sousArbre = new Set<string>([d.id])
  for (const x of dossiers) if (x.parentId && sousArbre.has(x.parentId)) sousArbre.add(x.id)
  const aEnfants = dossiers.some((x) => x.parentId === d.id)
  // Parent, principe d'ordre, hors période : seulement sur un dossier vide (fichiers rangés),
  // sauf découper en sous-dossiers un dossier qui reçoit les fichiers : ses pièces repartent.
  const vide = d.piecesSousArbre === 0
  const decoupable = !vide && d.ordre === 'fichiers'

  const agir = async (faire: () => Promise<unknown>) => {
    try {
      setErreur(null)
      await faire()
      await rafraichir()
    } catch (e) {
      setErreur(message(e))
      throw e
    }
  }
  const modifier = (json: Record<string, unknown>) =>
    agir(() => lire(api.dossiers[':id'].$patch({ param: { id: d.id }, json })))
  const enSilence = (p: Promise<unknown>) => p.catch(() => {})

  return (
    <div
      className={`dossier${d.enService ? '' : ' dossier-propose'}`}
      style={{ '--profondeur': d.profondeur } as CSSProperties}
    >
      <div className="dossier-tete">
        <Icone nom="folder" className="dossier-icone" />
        <div className="dossier-nom">
          {estRacine ? (
            <strong>Racine : {racine ? `« ${racine} »` : 'à renseigner sur la fiche'}</strong>
          ) : (
            <TexteEnLigne
              libelle="Nom du dossier"
              valeur={d.nom}
              lectureSeule={!ecriture}
              onEnregistrer={(nom) => modifier({ nom })}
            />
          )}
          <span className="dossier-badges">
            {d.statut === 'propose' && <span className="badge ton-ambre">Proposé</span>}
            {d.permanent && <span className="badge">Hors période</span>}
            {d.recoitFichiers && (
              <span className="badge mono">
                {d.pieces} pièce{d.pieces > 1 ? 's' : ''}
              </span>
            )}
            {d.nom !== d.nomDrive && d.statut === 'actif' && (
              <span className="badge ton-pointille">
                Renommage dans le Drive en cours (« {d.nomDrive} »)
              </span>
            )}
          </span>
        </div>
        {!estRacine && <span className="discret mono dossier-chemin">{d.chemin}</span>}
      </div>

      <div className="dossier-champs">
        <div className="dossier-champ">
          <span className="dossier-etiquette">À quoi sert le dossier</span>
          <TexteEnLigne
            libelle="À quoi sert le dossier"
            valeur={d.role}
            multiligne
            lectureSeule={!ecriture}
            onEnregistrer={(role) => modifier({ role })}
          />
        </div>
        <div className="dossier-champ">
          <span className="dossier-etiquette">Principe d’ordre et de classement</span>
          {ecriture ? (
            <select
              aria-label="Principe d’ordre"
              value={d.ordre}
              disabled={!vide && !decoupable}
              title={
                vide
                  ? undefined
                  : decoupable
                    ? 'Il contient des pièces : le découper en sous-dossiers les replace.'
                    : 'Ce dossier contient déjà des pièces rangées.'
              }
              onChange={(ev) => void enSilence(modifier({ ordre: ev.target.value }))}
            >
              {options(LIBELLES_ORDRE_DOSSIER)
                .filter((o) => vide || o.valeur === 'fichiers' || o.valeur === 'sous_dossiers')
                .map((o) => (
                  <option key={o.valeur} value={o.valeur}>
                    {o.libelle}
                  </option>
                ))}
            </select>
          ) : (
            <span>{LIBELLES_ORDRE_DOSSIER[d.ordre]}</span>
          )}
          <TexteEnLigne
            libelle="Précisions sur le classement"
            valeur={d.precisions}
            vide={ecriture ? 'Préciser (facultatif)' : 'Sans précision'}
            multiligne
            lectureSeule={!ecriture}
            onEnregistrer={(precisions) => modifier({ precisions: precisions || null })}
          />
          {parent && date(parent.ordre) && ecriture && (
            <label className="champ-case discret">
              <input
                type="checkbox"
                checked={d.permanent}
                disabled={!vide}
                onChange={(ev) => void enSilence(modifier({ permanent: ev.target.checked }))}
              />
              Hors période : directement sous {parent.nom ? `« ${parent.nom} »` : 'la racine'}, pas
              dans chaque {parent.ordre === 'par_exercice' ? 'exercice' : 'année'}
            </label>
          )}
        </div>
      </div>

      {d.recoitFichiers && d.enService && (d.refacturation || ecriture) && (
        <RegleRefacturation d={d} entiteId={entiteId} ecriture={ecriture} agir={agir} />
      )}

      <Proposition d={d} dossiers={dossiers} ecriture={ecriture} agir={agir} />

      {ecriture && (
        <div className="dossier-actions">
          {PEUT_CONTENIR.includes(d.ordre) && d.enService && (
            <button type="button" className="bouton-discret" onClick={() => setAjout(!ajout)}>
              <Icone nom="create_new_folder" />
              Sous-dossier
            </button>
          )}
          {!estRacine && vide && (
            <button type="button" className="bouton-discret" onClick={() => setDeplacer(!deplacer)}>
              <Icone nom="drive_file_move" />
              Déplacer
            </button>
          )}
          {!estRacine && vide && !aEnfants && d.statut === 'actif' && (
            <button
              type="button"
              className="bouton-discret bouton-danger"
              onClick={async () => {
                if (!(await confirmer(`Supprimer le dossier « ${d.nom} » du plan ?`))) return
                void enSilence(
                  agir(() => lire(api.dossiers[':id'].$delete({ param: { id: d.id } }))),
                )
              }}
            >
              <Icone nom="delete" />
              Supprimer
            </button>
          )}
        </div>
      )}

      {deplacer && (
        <div className="dossier-formulaire">
          <label className="champ">
            <span>Déplacer sous</span>
            <select
              defaultValue=""
              onChange={(ev) => {
                if (!ev.target.value) return
                void enSilence(
                  modifier({ parentId: ev.target.value }).then(() => setDeplacer(false)),
                )
              }}
            >
              <option value="">Choisir un dossier…</option>
              {dossiers
                .filter(
                  (x) =>
                    x.enService &&
                    PEUT_CONTENIR.includes(x.ordre) &&
                    !sousArbre.has(x.id) &&
                    x.id !== d.parentId,
                )
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.parentId === null ? 'Racine' : x.chemin}
                  </option>
                ))}
            </select>
          </label>
        </div>
      )}

      {ajout && (
        <NouveauDossier parent={d} entiteId={entiteId} fermer={() => setAjout(false)} agir={agir} />
      )}

      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

/**
 * Règle de refacturation du dossier : toute pièce qui y est classée, datée à partir du jour
 * choisi, devient une ligne « à refacturer » à cette entité ou à ce tiers (notes de frais).
 */
function RegleRefacturation({
  d,
  entiteId,
  ecriture,
  agir,
}: {
  d: Dossier
  entiteId: string
  ecriture: boolean
  agir: (faire: () => Promise<unknown>) => Promise<unknown>
}) {
  const entites = useQuery(requetes.entites).data ?? []
  const tiers = useQuery(requetes.tiers).data ?? []
  const [edition, setEdition] = useState(false)
  const [destinataire, setDestinataire] = useState('')
  const [depuis, setDepuis] = useState(`${new Date().getFullYear()}-01-01`)
  const [quotePart, setQuotePart] = useState('')
  const regle = d.refacturation
  const regler = (r: unknown) =>
    agir(() =>
      lire(
        api.dossiers[':id'].refacturation.$put({
          param: { id: d.id },
          json: { regle: corps(refacturationDossier, r) },
        }),
      ),
    )
  const nom = regle
    ? (entites.find((e) => e.id === regle.destinataireEntiteId)?.nom ??
      tiers.find((t) => t.id === regle.destinataireTiersId)?.nom ??
      'un destinataire')
    : null
  return (
    <div className="dossier-champ">
      <span className="dossier-etiquette">Frais à refacturer</span>
      {regle && !edition && (
        <span>
          Pièces à refacturer à <strong>{nom}</strong>
          {regle.quotePart !== null && regle.quotePart !== undefined && (
            <> pour {formaterQuotePart(regle.quotePart)} de leur montant</>
          )}
          , datées à partir du {formaterJour(regle.depuis)}.{' '}
          {ecriture && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() => void regler(null).catch(() => {})}
            >
              Retirer
            </button>
          )}
        </span>
      )}
      {!regle && !edition && ecriture && (
        <button type="button" className="bouton-discret" onClick={() => setEdition(true)}>
          <Icone nom="receipt_long" />
          Refacturer les pièces classées ici…
        </button>
      )}
      {edition && (
        <div className="dossier-formulaire">
          <label className="champ">
            <span>À refacturer à</span>
            <select value={destinataire} onChange={(ev) => setDestinataire(ev.target.value)}>
              <option value="">Choisir…</option>
              {entites
                .filter((e) => e.id !== entiteId && !e.archivee && !e.privee)
                .map((e) => (
                  <option key={e.id} value={`e:${e.id}`}>
                    {e.nom}
                  </option>
                ))}
              {tiers.map((t) => (
                <option key={t.id} value={`t:${t.id}`}>
                  {t.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="champ">
            <span>Pièces datées à partir du</span>
            <input type="date" value={depuis} onChange={(ev) => setDepuis(ev.target.value)} />
          </label>
          <label className="champ">
            <span>Quote-part (%), vide : tout</span>
            <input
              inputMode="decimal"
              placeholder="100"
              value={quotePart}
              onChange={(ev) => setQuotePart(ev.target.value)}
            />
          </label>
          <div className="dossier-actions">
            <button type="button" className="bouton-discret" onClick={() => setEdition(false)}>
              Annuler
            </button>
            <button
              type="button"
              className="bouton"
              disabled={!destinataire || !depuis}
              onClick={() => {
                const [sorte, id] = destinataire.split(':')
                const part = quotePart.trim().replace(',', '.')
                void regler({
                  ...(sorte === 'e' ? { destinataireEntiteId: id } : { destinataireTiersId: id }),
                  depuis,
                  ...(part && { quotePart: Number(part) }),
                })
                  .then(() => setEdition(false))
                  .catch(() => {})
              }}
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function NouveauDossier({
  parent,
  entiteId,
  fermer,
  agir,
}: {
  parent: Dossier
  entiteId: string
  fermer: () => void
  agir: (faire: () => Promise<unknown>) => Promise<unknown>
}) {
  const [nom, setNom] = useState('')
  const [role, setRole] = useState('')
  const [ordre, setOrdre] = useState<OrdreDossier>('fichiers')
  const [permanent, setPermanent] = useState(false)
  return (
    <form
      className="dossier-formulaire formulaire"
      onSubmit={(ev) => {
        ev.preventDefault()
        void agir(() =>
          lire(
            api.entites[':id'].plan.dossiers.$post({
              param: { id: entiteId },
              json: corps(dossierCreation, { parentId: parent.id, nom, role, ordre, permanent }),
            }),
          ),
        ).then(fermer, () => {})
      }}
    >
      <label className="champ">
        <span>Nom</span>
        <input value={nom} onChange={(e) => setNom(e.target.value)} required />
      </label>
      <label className="champ">
        <span>À quoi sert le dossier</span>
        <input value={role} onChange={(e) => setRole(e.target.value)} required />
      </label>
      <label className="champ">
        <span>Principe d’ordre</span>
        <select value={ordre} onChange={(e) => setOrdre(e.target.value as OrdreDossier)}>
          {options(LIBELLES_ORDRE_DOSSIER).map((o) => (
            <option key={o.valeur} value={o.valeur}>
              {o.libelle}
            </option>
          ))}
        </select>
      </label>
      {date(parent.ordre) && (
        <label className="champ-case discret">
          <input
            type="checkbox"
            checked={permanent}
            onChange={(e) => setPermanent(e.target.checked)}
          />
          Hors période
        </label>
      )}
      <div className="boutons">
        <button type="submit" className="bouton">
          Ajouter
        </button>
        <button type="button" className="bouton-discret" onClick={fermer}>
          Annuler
        </button>
      </div>
    </form>
  )
}

/** Ce que l'assistant propose sur ce dossier, et les deux gestes : valider, refuser. */
function Proposition({
  d,
  dossiers,
  ecriture,
  agir,
}: {
  d: Dossier
  dossiers: readonly Dossier[]
  ecriture: boolean
  agir: (faire: () => Promise<unknown>) => Promise<unknown>
}) {
  if (d.statut !== 'propose' && !d.proposition) return null
  const p = d.proposition
  const changements: string[] = []
  if (d.statut === 'propose') changements.push('Nouveau dossier')
  if (p?.supprimer) changements.push('Supprimer ce dossier')
  if (p?.nom !== undefined) changements.push(`Nom : « ${p.nom} »`)
  if (p?.role !== undefined) changements.push(`À quoi il sert : ${p.role}`)
  if (p?.ordre !== undefined)
    changements.push(`Principe d’ordre : ${LIBELLES_ORDRE_DOSSIER[p.ordre]}`)
  if (p?.precisions !== undefined) changements.push(`Précisions : ${p.precisions ?? 'aucune'}`)
  if (p?.parentId !== undefined) {
    const vers = dossiers.find((x) => x.id === p.parentId)
    changements.push(
      `Déplacer sous : ${vers?.parentId === null ? 'la racine' : (vers?.chemin ?? '?')}`,
    )
  }
  if (p?.permanent !== undefined) changements.push(p.permanent ? 'Hors période' : 'Dans la période')
  const geste = (quoi: 'valider' | 'refuser') => () =>
    void agir(() => lire(api.dossiers[':id'][quoi].$post({ param: { id: d.id } }))).catch(() => {})
  return (
    <div className="dossier-proposition">
      <p>
        <Icone nom="smart_toy" />
        <strong>Proposé par {d.proposePar ?? 'l’assistant'}</strong>
        {d.motif && <span className="discret"> : {d.motif}</span>}
      </p>
      <ul className="liste-simple">
        {changements.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      {ecriture && (
        <div className="boutons">
          <button type="button" className="bouton" onClick={geste('valider')}>
            <Icone nom="check" />
            Valider
          </button>
          <button type="button" className="bouton-discret" onClick={geste('refuser')}>
            <Icone nom="close" />
            Refuser
          </button>
        </div>
      )}
    </div>
  )
}
